import { useCanonicalUser } from "@/hooks/useCanonicalUser";

// Canonical Identity Phase 1 (2026-09-10) — 한 canonical user 의 연결된 identity 와
// PROD/STG 환경별 wallet 을 표시한다. 안전 규칙(설계 승인 사항):
//   · PROD/STG 는 반드시 별도 섹션으로만 렌더. PROD+STG 를 하나의 숫자로 합치지 않는다.
//   · displayTotal 은 "표시용 계산값"이며 실제 wallet 이 아님을 화면에 명시한다.
//   · sub 별 세부값을 항상 병기(어느 sub 에 얼마인지 운영자가 항상 볼 수 있게).
const nf = new Intl.NumberFormat("ko-KR");
const ENV_LABELS = { prod: "PROD", stg: "STG" };

function WalletEnvSection({ envKey, block }) {
  if (!block) return null;
  const subs = block.subs || [];
  const total = block.displayTotal || { balance: 0, totalCharged: 0, totalUsed: 0 };
  return (
    <section style={{ marginTop: 16 }}>
      <h4>{ENV_LABELS[envKey] || envKey} wallets</h4>
      {subs.length === 0 ? (
        <p>연결된 지갑 없음</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>sub</th><th>pool</th><th>balance</th><th>plan</th>
              <th>totalCharged</th><th>totalUsed</th><th>found</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={`${envKey}-${s.sub}`}>
                <td title={s.sub}>{s.sub}</td>
                <td>{s.pool}</td>
                <td>{nf.format(s.balance)}</td>
                <td>{s.plan || "-"}</td>
                <td>{nf.format(s.totalCharged)}</td>
                <td>{nf.format(s.totalUsed)}</td>
                <td>{s.found ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p>
        <strong>{ENV_LABELS[envKey] || envKey} 합계(표시용)</strong>: balance {nf.format(total.balance)}
        {" · "}charged {nf.format(total.totalCharged)}{" · "}used {nf.format(total.totalUsed)}
        <br />
        <small>표시용 계산값이며 실제 wallet 이 아닙니다. PROD/STG 잔액은 합치지 않습니다.</small>
      </p>
    </section>
  );
}

export default function CanonicalUserView({ canonicalUserId, onFallback }) {
  const { data, loading, notFound, error } = useCanonicalUser(canonicalUserId);

  // 매핑 없음 → 호출부가 기존 sub 조회로 fallback 하도록 신호만 주고 아무것도 안 그린다.
  if (notFound) {
    if (typeof onFallback === "function") onFallback();
    return null;
  }
  if (loading) return <p>불러오는 중…</p>;
  if (error) return <p>조회 실패: {error.message}</p>;
  if (!data) return null;

  const wallets = data.walletsByEnv || {};
  return (
    <div>
      <header>
        <h3>Canonical User {data.canonicalUserId}</h3>
        <p>
          유형: {data.accountType || "-"}{" · "}대표이메일: {data.primaryEmail || "-"}
          {" · "}상태: {data.status || "-"}
        </p>
      </header>

      <section>
        <h4>연결된 로그인(identities)</h4>
        {(data.identities || []).length === 0 ? (
          <p>연결된 identity 없음</p>
        ) : (
          <table>
            <thead>
              <tr><th>sub</th><th>pool</th><th>provider</th><th>email</th><th>verified</th><th>status</th></tr>
            </thead>
            <tbody>
              {data.identities.map((it) => (
                <tr key={it.identityKey || it.sub}>
                  <td title={it.sub}>{it.sub}</td>
                  <td>{it.pool}</td>
                  <td>{it.provider || "-"}</td>
                  <td>{it.email || "-"}</td>
                  <td>{it.emailVerified ? "✓" : "—"}</td>
                  <td>{it.status || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* PROD/STG 는 별도 섹션. 합산 금지 규칙 준수. */}
      <WalletEnvSection envKey="prod" block={wallets.prod} />
      <WalletEnvSection envKey="stg" block={wallets.stg} />
    </div>
  );
}
